<template>
  <the-content v-model="showFilters">

    <template v-slot:content>
      <search class="mh2-l mv2"
        :placeholder="searchPlaceholder"
        :init-value="validParams.search"
        @search="runSearch"
        @reset="resetSearch"
      />
      <indeterminate-line v-if="loading" class="mh2-l"/>
      <div v-else-if="procedures.length > 0">
        <div class="list-grid mh2-l mt2">
          <list-item v-for="(proc, index) in procedures" :key="index" :proc="proc" />
        </div>
        <transition name="fade">
          <div v-if="loadingStages" class="mh2-l mv3 text-secondary flex flex-row justify-center items-center">
            <content-spinner class="db" :size="20"/>
            <span class="db ml3">поиск этапов процедуры...</span>
          </div>
          <div v-else-if="procedureStages && procedureStages.length > 0" class="mh2-l mt2">
            <span class="db mt4 mb3 ttu f6 text-primary b">все этапы</span>
            <div class="list-grid">
              <list-item v-for="(proc, index) in procedureStages" :key="index" :proc="proc" :highlight="proc.id === procedures[0].id"/>
            </div>
          </div>
        </transition>
        <div class="pv3 flex justify-center">
          <paginator :page="validParams.page" routing="false" :total="totalCount" @goto="paginatorRoute"/>
        </div>
      </div>
      <div v-else class="pa2 f6 ttl silver">
        По вашему запросу ничего не найдено
      </div>
    </template>

    <template v-slot:sidebar>
      <filters
        :department="validParams.department"
        :manager="validParams.manager"
        :direction="validParams.direction"
        :checked-statuses="validParams.statuses"
        :stats="stats"
        :sort="validParams.sort"
        @updated="filterRoute"
      />
    </template>

    <template v-slot:mobile-sidebar>
      <filters
        :department="validParams.department"
        :manager="validParams.manager"
        :direction="validParams.direction"
        :checked-statuses="validParams.statuses"
        :stats="stats"
        :sort="validParams.sort"
        @updated="filterRoute"
      />
    </template>

  </the-content>
</template>

<script>
import TheContent from '@/components/Content.vue'
import Search from '@/components/common/Search.vue'
import IndeterminateLine from '@/components/ui/progress/IndeterminateLine.vue'
import ContentSpinner from '@/components/ui/progress/ContentSpinner.vue'
import Filters from '@/components/procedures/Filters.vue'
import ListItem from '@/components/procedures/ListItem.vue'
import Paginator from '@/components/Paginator.vue'
import { DataService } from '@/services/DataService.js'
import { mapBackendProceduresToFrontend } from '@/utils/procedure.js'
import { mapGetters } from 'vuex'

export default {
  components: {
    TheContent,
    Search,
    IndeterminateLine,
    ContentSpinner,
    Filters,
    ListItem,
    Paginator
  },
  props: {
    params: { type: Object }
  },
  watch: {
    validParams: {
      immediate: true,
      handler: function (validParams) {
        const procTypes = { 1: 'BUY', 2: 'SELL' }
        const apiParams = {
          companyId: this.user.companyId,
          search: validParams.search,
          ...validParams.direction > 0 && { type: procTypes[validParams.direction] },
          ...validParams.department && { departmentId: validParams.department },
          ...validParams.manager && { employeeId: validParams.manager },
          ...validParams.statuses.length > 0 && { states: validParams.statuses },
          sort: validParams.sort.toUpperCase(),
          offset: (+validParams.page - 1) * this.limit,
          limit: this.limit
        }
        this.loading = true
        Promise.all([this.loadProcedures(apiParams), this.loadProcedureStats(apiParams)])
          .finally(() => {
            // TODO: это все (this.totalProcedures =) хаки, так как метод getProcedures не возвращает общее кол-во элементов
            // соответствующим условиям фильтров
            if (apiParams.search) {
              this.totalProcedures = this.procedures ? this.procedures.length : 0
            } else {
              this.totalProcedures = this.stats
                ? this.validParams.statuses.length > 0
                  ? this.validParams.statuses.reduce((acc, v) => acc + this.stats[v], 0)
                  : Object.values(this.stats).reduce((acc, v) => acc + v, 0)
                : null
            }
            if (apiParams.search && this.procedures.length === 1) {
              this.loadingStages = true
              this.loadProcedureStages(this.procedures[0].id)
                .finally(() => {
                  this.loadingStages = false
                })
            }
            this.loading = false
          })
      }
    }
  },
  data () {
    return {
      showFilters: false,
      searchPlaceholder: 'поиск по номеру процедуры',
      procedures: [],
      procedureStages: [],
      totalProcedures: 0,
      stats: null,
      limit: 25,
      loading: false,
      loadingStages: false
    }
  },
  computed: {
    ...mapGetters('session', ['user']),
    validParams () {
      const statuses = Array.isArray(this.params.statuses)
        ? this.params.statuses.map((s) => +s)
        : Number.isInteger(+this.params.statuses) ? [+this.params.statuses] : []
      return {
        department: +this.params.department,
        manager: +this.params.manager,
        direction: +this.params.direction || 0,
        statuses,
        search: this.params.search,
        page: this.params.page || 1,
        sort: this.params.sort || 'desc'
      }
    },
    totalCount () {
      return this.totalProcedures % this.limit
        ? Math.floor(this.totalProcedures / this.limit) + 1
        : this.totalProcedures / this.limit
    }
  },
  methods: {
    paginatorRoute (where) {
      this.$router.push({ name: this.$router.name, query: this.filterEmpty({ ...this.validParams, page: where.query.page }) })
    },
    filterRoute (update) {
      this.$router.push({ name: this.$router.name, query: this.filterEmpty({ ...this.validParams, ...update, page: 1 }) })
    },
    runSearch (value) {
      this.$router.push({ name: this.$router.name, query: this.filterEmpty({ ...this.validParams, ...{ search: value }, page: 1 }) })
    },
    resetSearch () {
      this.$router.push({ name: this.$router.name, query: this.filterEmpty({ ...this.validParams, ...{ search: null }, page: 1 }) })
    },
    filterEmpty (params) {
      return this.$lo.pickBy(params, (value, key) => value)
    },
    loadProcedures (params) {
      this.procedureStages = []
      return DataService.getProcedures(params)
        .then(({ data: { procedures } }) => {
          this.procedures = mapBackendProceduresToFrontend(procedures) || []
        })
    },
    loadProcedureStats (params) {
      delete params.states
      return DataService.getProceduresStats(params)
        .then(({ data: { procedures_stats: stats } }) => {
          this.stats = stats
            ? stats.reduce(function (map, obj) {
              map[obj.group] = obj.count || 0
              return map
            }, {})
            : null
        })
    },
    loadProcedureStages (procedureId) {
      return DataService.getProcedureStages({ procedureId })
        .then(({ data: { procedureStages }, errors: sErrors }) => {
          if (procedureStages && procedureStages.length > 1) {
            const search = procedureStages.map(s => s.procedureId).join()
            return DataService.getProcedures({ search })
              .then(({ data: { procedures } }) => {
                // сохраняем все этапы включая найденную процедуру в this.procedureStages
                this.procedureStages = procedures
                  .map(p => ({
                    ...p,
                    stage: procedureStages.find(s => s.procedureId === p.id)
                  }))
                  .sort((l, r) => l.stage.stage - r.stage.stage)
                // ставим этап в найденную изначально процедуру
                this.procedures = this.procedures
                  .map(p => ({
                    ...p,
                    stage: procedureStages.find(s => s.procedureId === p.id)
                  }))
              })
          } else {
            return Promise.resolve({})
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
